import useResizeObserver from "@react-hook/resize-observer"
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Image as AImage,
  message,
  Modal,
  Row,
  Table,
  Tooltip,
  Upload,
  Select,
  Tag,
  Input,
  Descriptions,
  Tabs,
  Badge,
  Dropdown,
  Radio,
} from "antd"
import * as API from "common/api"
import * as COMMONS from "common/common"
import RESIZE_FILE from "common/resizer"
import moment from "moment"
import "moment/locale/ja"
import React, { useEffect, useRef, useState } from "react"
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd"
import { Helmet } from "react-helmet-async"
import {
  FaCamera,
  FaEye,
  FaEyeSlash,
  FaTrashAlt,
  FaPlus,
  FaFileCsv,
  FaArrowLeft,
  FaArrowRight,
  FaChevronDown,
  FaSyncAlt,
} from "react-icons/fa"
import { MdEdit } from "react-icons/md"
import { useCSVReader } from "react-papaparse"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useNavigate } from "react-router-dom"
import styled from "styled-components"
import {
  ButtonBack,
  ButtonNext,
  CarouselProvider,
  Dot,
  Image,
  Slide,
  Slider,
} from "pure-react-carousel"
import "pure-react-carousel/dist/react-carousel.es.css"
import fileDownload from "js-file-download"

moment.updateLocale("ja")

const CustomUpload = styled(Upload)`
  .ant-upload {
    width: 100%;
    max-height: "200px";
  }
`

const CustomTable = styled(Table)`
  .ant-table-ping-right,
  .ant-table-ping-left .ant-table-thead {
    cursor: grab;
    cursor: -webkit-grab;
  }

  .ant-table-thead.active {
    cursor: grabbing;
    cursor: -webkit-grabbing;
  }
`

const CustomDotGroup = styled.div`
  & {
    display: block;
    text-align: center;
  }
  .carousel__dot {
    border: 1px solid ${COMMONS.PRIMARY_COLOR};
    border-radius: 0.25rem;
  }
  .carousel__dot--selected {
    border: 1px solid ${COMMONS.PRIMARY_COLOR};
    border-radius: 0.25rem;
    opacity: 0.4;
  }
`

const { Option } = Select
const { TabPane } = Tabs

const exportOptions = [
  {
    key: "products",
    label: "商品リスト",
  },
  {
    type: "divider",
  },
  {
    key: "sample",
    label: "商品登録サンプル",
  },
]

const useSize = (target) => {
  const [size, setSize] = React.useState()

  React.useLayoutEffect(() => {
    setSize(target.current.getBoundingClientRect())
  }, [target])

  useResizeObserver(target, (entry) => setSize(entry.contentRect))
  return size
}

const Products = (props) => {
  const navigate = useNavigate()

  const isMountedRef = COMMONS.USE_IS_MOUNTED_REF()
  const queryClient = useQueryClient()
  const { CSVReader } = useCSVReader()

  const [productForm] = Form.useForm()
  const [productsFilterForm] = Form.useForm()

  const tableRef = useRef(null)
  const size = useSize(tableRef)
  const [isScrollableHintVisible, setIsScrollableHintVisible] = useState(false)

  const [products, setProducts] = useState([])
  const [currentProduct, setCurrentProduct] = useState(undefined)
  const [productImages, setProductImages] = useState([])

  const [csvProducts, setCsvProducts] = useState([])
  const [csvValidProducts, setCsvValidProducts] = useState([])
  const [csvInvalidProducts, setCsvInvalidProducts] = useState([])
  const [csvHasError, setCsvHasError] = useState(false)

  const [isProductModalVisible, setIsProductModalVisible] = useState(false)
  const [isProductDetailModalVisible, setIsProductDetailModalVisible] =
    useState(false)
  const [isImportProductModalVisible, setIsImportProductModalVisible] =
    useState(false)
  const [
    isImportProductConfirmModalVisible,
    setIsImportProductConfirmModalVisible,
  ] = useState(false)

  const [paginationPerPage, setPaginationPerPage] = useState(20)
  const [paginationPage, setPaginationPage] = useState(1)
  const [paginationTotal, setPaginationTotal] = useState(0)
  const [paginationSort, setPaginationSort] = useState("asc")
  const [paginationSortKey, setPaginationSortKey] = useState("displayOrder")

  const [filterProductCode, setFilterProductCode] = useState(undefined)
  const [filterProductName, setFilterProductName] = useState(undefined)
  const [filterIsDisplayed, setFilterIsDisplayed] = useState(undefined)

  useQuery(
    [
      API.QUERY_KEY_ADMIN_PRODUCTS,
      {
        paginationPerPage: paginationPerPage,
        paginationPage: paginationPage,
        paginationSort: paginationSort,
        paginationSortKey: paginationSortKey,
        filterProductCode: filterProductCode,
        filterProductName: filterProductName,
        filterIsDisplayed: filterIsDisplayed,
      },
    ],
    () =>
      API.ADMIN_GET_PRODUCTS(
        paginationPerPage,
        paginationPage,
        paginationSort,
        paginationSortKey,
        filterProductCode,
        filterProductName,
        filterIsDisplayed,
      ),
    {
      onSuccess: (response) => {
        if (isMountedRef.current) {
          if (response?.data?.rows && response?.data?.rows?.length > 0) {
            setProducts(response.data.rows)
          } else {
            setProducts([])
          }

          setPaginationPerPage(response.data.pp || 20)
          setPaginationPage(response.data.p || 1)
          setPaginationTotal(response?.data?.count || 0)
          setPaginationSort(response?.data?.sort || "asc")
          setPaginationSortKey(response?.data?.sortKey || "displayOrder")
        }
      },
      onError: (error) => {
        if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
          navigate(COMMONS.PERMISSION_ERROR_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
          message.warning(COMMONS.ERROR_SESSION_MSG)
          navigate(COMMONS.ADMIN_LOGIN_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        } else {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        }
      },
    },
  )

  const updateProductMutation = useMutation(API.ADMIN_UPDATE_PRODUCT, {
    onSuccess: () => {
      if (isMountedRef.current) {
        message.success(COMMONS.SUCCESS_SAVE_MSG)
        queryClient.invalidateQueries(API.QUERY_KEY_ADMIN_PRODUCTS)
        hideProductModal()
      }
    },
    onError: (error) => {
      if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
        navigate(COMMONS.PERMISSION_ERROR_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
        message.warning(COMMONS.ERROR_SESSION_MSG)
        navigate(COMMONS.ADMIN_LOGIN_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      } else {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      }
    },
  })

  const importProductsMutation = useMutation(API.ADMIN_IMPORT_PRODUCTS, {
    onError: (error) => {
      if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
        navigate(COMMONS.PERMISSION_ERROR_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
        message.warning(COMMONS.ERROR_SESSION_MSG)
        navigate(COMMONS.ADMIN_LOGIN_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      } else {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      }
    },
  })

  const exportProductCSVMutation = useMutation(API.ADMIN_EXPORT_PRODUCT_CSV, {
    onSuccess: (response) => {
      if (isMountedRef.current) {
        if (response?.data) {
          fileDownload(
            "\uFEFF" + response.data,
            `${moment().format("YYYYMMDDHHmm")}_商品リスト.csv`,
            "text/csv",
          )
        }
      }
    },
    onError: (error) => {
      if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
        navigate(COMMONS.PERMISSION_ERROR_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
        message.warning(COMMONS.ERROR_SESSION_MSG)
        navigate(COMMONS.ADMIN_LOGIN_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      } else {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      }
    },
  })

  const exportProductSampleCSVMutation = useMutation(
    API.ADMIN_EXPORT_PRODUCT_CSV,
    {
      onSuccess: (response) => {
        if (isMountedRef.current) {
          if (response?.data) {
            fileDownload(
              "\uFEFF" + response.data,
              `商品登録サンプル.csv`,
              "text/csv",
            )
          }
        }
      },
      onError: (error) => {
        if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
          navigate(COMMONS.PERMISSION_ERROR_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
          message.warning(COMMONS.ERROR_SESSION_MSG)
          navigate(COMMONS.ADMIN_LOGIN_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        } else {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        }
      },
    },
  )

  const syncPriceMutation = useMutation(API.ADMIN_SYNC_PRICE, {
    onError: (error) => {
      if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
        navigate(COMMONS.PERMISSION_ERROR_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
        message.warning(COMMONS.ERROR_SESSION_MSG)
        navigate(COMMONS.ADMIN_LOGIN_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      } else {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      }
    },
  })

  useEffect(() => {
    const slider = document.querySelector("thead.ant-table-thead")
    const body = document.querySelector(".ant-table-container .ant-table-body")
    let isDown = false
    let startX
    let scrollLeft

    slider.addEventListener("mousedown", (e) => {
      isDown = true
      slider.classList.add("active")
      startX = e.pageX - body.offsetLeft
      scrollLeft = body.scrollLeft
    })

    slider.addEventListener("mouseleave", () => {
      isDown = false
      slider.classList.remove("active")
    })

    slider.addEventListener("mouseup", () => {
      isDown = false
      slider.classList.remove("active")
    })

    slider.addEventListener("mousemove", (e) => {
      if (!isDown) {
        return
      }

      e.preventDefault()
      const x = e.pageX - body.offsetLeft
      const walk = (x - startX) * 3 //scroll-fast
      body.scrollLeft = scrollLeft - walk
    })
  }, [])

  useEffect(() => {
    if (
      tableRef?.current?.querySelector(".ant-table-ping-right") ||
      tableRef?.current?.querySelector(".ant-table-ping-left")
    ) {
      setIsScrollableHintVisible(true)
    } else {
      setIsScrollableHintVisible(false)
    }
  }, [size])

  const showProductDetailModal = (product) => {
    setCurrentProduct(product)
    setIsProductDetailModalVisible(true)
  }

  const hideProductDetailModal = () => {
    setCurrentProduct(undefined)
    setIsProductDetailModalVisible(false)
  }

  const showProductModal = (product) => {
    setCurrentProduct(product)
    setProductImages(product?.productImages || [])

    productForm.setFieldsValue({
      isDisplayed:
        product?.isDisplayed !== undefined ? product.isDisplayed + "" : "",
      productImages: product?.productImages
        ? product.productImages.map((image) => {
            return { image: image?.picUrl }
          })
        : undefined,
      productCountry: product.country,
      productRoasts: product.productRoasts
        ? product.productRoasts.map((roast) => roast.label)
        : [],
      productRecommendedRoast: product.recommendedRoast,
      productRecommendedRoastText: product.recommendedRoastText,
      productGrinds: product.productGrinds
        ? product.productGrinds.map((grind) => grind.label)
        : [],
      productTastes: product.productTastes
        ? product.productTastes.map((taste) => taste.label)
        : [],
    })

    setIsProductModalVisible(true)
  }

  const hideProductModal = () => {
    setCurrentProduct(undefined)
    setProductImages([])
    setIsProductModalVisible(false)
  }

  const showImportProductModal = () => {
    setIsImportProductModalVisible(true)
  }

  const hideImportProductModal = () => {
    setCsvProducts([])
    setCsvHasError(false)
    setIsImportProductModalVisible(false)
  }

  const showImportProductConfirmModal = () => {
    setIsImportProductConfirmModalVisible(true)
  }

  const hideImportProductConfirmModal = () => {
    setCsvValidProducts([])
    setCsvInvalidProducts([])
    setIsImportProductConfirmModalVisible(false)
  }

  const handleUpdateProduct = (data) => {
    const paramData = {
      isDisplayed: data.isDisplayed + "",
      productId: currentProduct?.productId,
      picUrl: productImages
        .filter((image) => image?.file)
        .map((image) => image?.file),
      picData: productImages.map((image, i) => {
        return JSON.stringify({
          originalName: image?.file?.name || image?.picUrl,
          order: i,
        })
      }),
      country: data.productCountry,
      roasts: data.productRoasts,
      recommendedRoast: data.productRecommendedRoast,
      recommendedRoastText: data.productRecommendedRoastText,
      grinds: data.productGrinds,
      tastes: data.productTastes,
    }

    updateProductMutation.mutate(paramData)
  }

  const handleImportProduct = () => {
    const paramData = {
      products: csvProducts
        .filter((row) => row?.productCode)
        .map((row) => ({
          ...row,
          isDisplayed:
            row?.isDisplayed === "1"
              ? true
              : row?.isDisplayed === "0"
                ? false
                : undefined,
          productRoasts:
            row?.productRoasts && row?.productRoasts?.split(",")?.length > 0
              ? row.productRoasts.split(",")
              : [],
          productGrinds:
            row?.productGrinds && row?.productGrinds?.split(",")?.length > 0
              ? row.productGrinds.split(",")
              : [],
          productTastes:
            row?.productTastes && row?.productTastes?.split(",")?.length > 0
              ? row.productTastes.split(",")
              : [],
        })),
    }

    importProductsMutation.mutate(paramData, {
      onSuccess: (response) => {
        if (isMountedRef.current) {
          message.success(COMMONS.SUCCESS_CONFIRM_MSG)
          hideImportProductModal()

          setCsvValidProducts(response?.data?.valid || [])
          setCsvInvalidProducts(response?.data?.invalid || [])

          showImportProductConfirmModal()

          // message.success(COMMONS.SUCCESS_IMPORT_MSG)
          // queryClient.invalidateQueries(API.QUERY_KEY_ADMIN_PRODUCTS)
          // hideImportProductModal()
        }
      },
    })
  }

  const handleImportProductConfirm = () => {
    const paramData = {
      mode: "confirm",
      products: csvValidProducts,
    }

    importProductsMutation.mutate(paramData, {
      onSuccess: (response) => {
        if (isMountedRef.current) {
          message.success(COMMONS.SUCCESS_IMPORT_MSG)
          hideImportProductConfirmModal()
          queryClient.invalidateQueries(API.QUERY_KEY_ADMIN_PRODUCTS)
        }
      },
    })
  }

  const handleExportProduct = () => {
    const paramData = {
      isSample: false,
    }

    exportProductCSVMutation.mutate(paramData)
  }

  const handleExportProductSample = () => {
    const paramData = {
      isSample: true,
    }

    exportProductSampleCSVMutation.mutate(paramData)
  }

  const exportHandler = ({ key }) => {
    if (key === "products") {
      handleExportProduct()
    } else if (key === "sample") {
      handleExportProductSample()
    }
  }

  const handlePriceSync = () => {
    const paramData = {}

    syncPriceMutation.mutate(paramData, {
      onSuccess: (response) => {
        if (isMountedRef.current) {
          message.success(COMMONS.SUCCESS_SYNC_MSG)
          queryClient.invalidateQueries(API.QUERY_KEY_ADMIN_PRODUCTS)
        }
      },
    })
  }

  const productImageOnDragEndHandle = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return
    } else {
      const orderedFormProductImages = COMMONS.RE_ORDER(
        productForm.getFieldValue("productImages"),
        result.source.index,
        result.destination.index,
      )

      const orderedFileProductImages = COMMONS.RE_ORDER(
        productImages,
        result.source.index,
        result.destination.index,
      )

      productForm.setFieldsValue({
        productImages: orderedFormProductImages,
      })

      setProductImages(orderedFileProductImages)
    }
  }

  const productColumns = [
    {
      key: "表示",
      title: <p className="font-bold bg-secondary rounded">表示</p>,
      dataIndex: "isDisplayed",
      align: "center",
      width: 50,
      render: (isDisplayed) =>
        isDisplayed ? (
          <FaEye className="text-primary text-lg" />
        ) : (
          <FaEyeSlash className="text-red-600 text-lg" />
        ),
    },
    {
      key: "productId",
      title: "ID",
      dataIndex: "productId",
      align: "center",
      width: 80,
      sorter: () => {},
    },
    {
      key: "商品画像",
      title: (
        <p className="font-bold bg-secondary rounded whitespace-pre-wrap">{`商品\n画像`}</p>
      ),
      dataIndex: "productImages",
      align: "center",
      width: 70,
      render: (productImages) =>
        productImages && productImages.length > 0 ? (
          <AImage
            src={`${API.UPLOADS_URL}${productImages[0]?.picUrl || ""}`}
            preview={false}
            alt="商品画像"
            className="rounded object-contain"
          />
        ) : (
          <AImage
            preview={false}
            src="/default.jpg"
            alt="商品画像"
            className="rounded object-contain"
          />
        ),
    },
    {
      key: "商品コード",
      title: <p className="whitespace-pre-wrap">{`商品\nコード`}</p>,
      dataIndex: "productCode",
      align: "center",
      width: 80,
      sorter: () => {},
    },
    {
      key: "品番",
      title: "品番",
      dataIndex: "supplierProductNo",
      align: "center",
      width: 80,
      sorter: () => {},
    },
    {
      key: "部門名",
      title: "部門名",
      dataIndex: "productCategory",
      align: "center",
      width: 80,
    },
    {
      key: "商品名",
      title: "商品名",
      dataIndex: "productName",
      align: "center",
      width: 150,
      sorter: () => {},
    },
    {
      key: "原価",
      title: "原価",
      dataIndex: "cost",
      align: "center",
      width: 80,
      sorter: () => {},
      render: (cost) =>
        `${COMMONS.CURRENCY}${(Number(cost) || 0).toLocaleString()}`,
    },
    {
      key: "商品単価",
      title: <p className="whitespace-pre-wrap">{`商品\n単価`}</p>,
      dataIndex: "price",
      align: "center",
      width: 80,
      sorter: () => {},
      render: (price) =>
        `${COMMONS.CURRENCY}${(Number(price) || 0).toLocaleString()}`,
    },
    {
      title: <p className="font-bold bg-secondary rounded">追加設定</p>,
      align: "center",
      children: [
        {
          key: "原産国",
          title: <p className="font-bold bg-secondary rounded">原産国</p>,
          dataIndex: "country",
          align: "center",
          width: 80,
        },
        {
          key: "おすすめの焙煎度",
          title: (
            <p className="font-bold bg-secondary rounded whitespace-pre-wrap">{`おすすめ\n焙煎度`}</p>
          ),
          dataIndex: "recommendedRoast",
          align: "center",
          width: 80,
        },
        {
          key: "焙煎度",
          title: <p className="font-bold bg-secondary rounded">焙煎度</p>,
          dataIndex: "productRoasts",
          align: "center",
          width: 80,
          render: (productRoasts) => (
            <>
              {productRoasts && productRoasts.length > 0
                ? productRoasts.map((roast) => (
                    <Tag
                      key={roast?.label}
                      color={COMMONS.PRIMARY_COLOR}
                      className="m-1"
                    >
                      {roast?.label || "ー"}
                    </Tag>
                  ))
                : "ー"}
            </>
          ),
        },
        {
          key: "挽き目",
          title: <p className="font-bold bg-secondary rounded">挽き目</p>,
          dataIndex: "productGrinds",
          align: "center",
          width: 80,
          render: (productGrinds) => (
            <>
              {productGrinds && productGrinds.length > 0
                ? productGrinds.map((grind) => (
                    <Tag
                      key={grind?.label}
                      color={COMMONS.PRIMARY_COLOR}
                      className="m-1"
                    >
                      {grind?.label || "ー"}
                    </Tag>
                  ))
                : "ー"}
            </>
          ),
        },
        {
          key: "特徴",
          title: <p className="font-bold bg-secondary rounded">特徴</p>,
          dataIndex: "productTastes",
          align: "center",
          width: 80,
          render: (productTastes) => (
            <>
              {productTastes && productTastes.length > 0
                ? productTastes.map((taste) => (
                    <Tag
                      key={taste?.label}
                      color={COMMONS.PRIMARY_COLOR}
                      className="whitespace-normal m-1"
                    >
                      {taste?.label || "ー"}
                    </Tag>
                  ))
                : "ー"}
            </>
          ),
        },
      ],
    },
    {
      key: "action",
      title: "",
      dataIndex: "action",
      align: "center",
      width: 100,
      render: (product) => (
        <>
          <Tooltip title="詳細" placement="top">
            <Button
              className="m-1"
              icon={<FaEye className="inline-block anticon" />}
              onClick={() => showProductDetailModal(product)}
            />
          </Tooltip>
          <Tooltip title="変更" placement="top">
            <Button
              className="m-1"
              icon={<MdEdit className="inline-block anticon" />}
              onClick={() => showProductModal(product)}
            />
          </Tooltip>
        </>
      ),
    },
  ]

  const importProductColumns = [
    {
      title: <p className="whitespace-pre-wrap">{`商品\nコード`}</p>,
      dataIndex: "productCode",
      align: "center",
      width: 80,
    },
    {
      title: "表示",
      dataIndex: "isDisplayed",
      align: "center",
      width: 80,
      render: (isDisplayed) =>
        isDisplayed === "1" ? (
          <FaEye className="text-primary text-lg" />
        ) : isDisplayed === "0" ? (
          <FaEyeSlash className="text-red-600 text-lg" />
        ) : (
          isDisplayed
        ),
    },
    {
      title: "原産国",
      dataIndex: "country",
      align: "center",
      width: 80,
    },
    {
      title: <p className="whitespace-pre-wrap">{`おすすめ\n焙煎度`}</p>,
      dataIndex: "recommendedRoast",
      align: "center",
      width: 80,
    },
    {
      title: <p className="whitespace-pre-wrap">{`おすすめ\n焙煎度の説明`}</p>,
      dataIndex: "recommendedRoastText",
      align: "center",
      width: 80,
    },
    {
      title: "焙煎度",
      dataIndex: "productRoasts",
      align: "center",
      width: 80,
      render: (productRoasts) => (
        <>
          {productRoasts && productRoasts?.split(",")?.length > 0
            ? productRoasts.split(",").map((roast) => (
                <Tag key={roast} color={COMMONS.PRIMARY_COLOR} className="m-1">
                  {roast || "ー"}
                </Tag>
              ))
            : "ー"}
        </>
      ),
    },
    {
      title: "挽き目",
      dataIndex: "productGrinds",
      align: "center",
      width: 80,
      render: (productGrinds) => (
        <>
          {productGrinds && productGrinds?.split(",")?.length > 0
            ? productGrinds.split(",").map((grind) => (
                <Tag key={grind} color={COMMONS.PRIMARY_COLOR} className="m-1">
                  {grind || "ー"}
                </Tag>
              ))
            : "ー"}
        </>
      ),
    },
    {
      title: "特徴",
      dataIndex: "productTastes",
      align: "center",
      width: 80,
      render: (productTastes) => (
        <>
          {productTastes && productTastes?.split(",")?.length > 0
            ? productTastes.split(",").map((taste) => (
                <Tag
                  key={taste}
                  color={COMMONS.PRIMARY_COLOR}
                  className="whitespace-normal m-1"
                >
                  {taste || "ー"}
                </Tag>
              ))
            : "ー"}
        </>
      ),
    },
  ]

  const importProductConfirmColumns = [
    {
      title: <p className="whitespace-pre-wrap">{`商品\nコード`}</p>,
      dataIndex: "productCode",
      align: "center",
      width: 80,
    },
    {
      title: "表示",
      dataIndex: "isDisplayed",
      align: "center",
      width: 80,
      render: (isDisplayed) =>
        isDisplayed ? (
          <FaEye className="text-primary text-lg" />
        ) : (
          <FaEyeSlash className="text-red-600 text-lg" />
        ),
    },
    {
      title: "原産国",
      dataIndex: "country",
      align: "center",
      width: 80,
    },
    {
      title: <p className="whitespace-pre-wrap">{`おすすめ\n焙煎度`}</p>,
      dataIndex: "recommendedRoast",
      align: "center",
      width: 80,
    },
    {
      title: <p className="whitespace-pre-wrap">{`おすすめ\n焙煎度の説明`}</p>,
      dataIndex: "recommendedRoastText",
      align: "center",
      width: 80,
    },
    {
      title: "焙煎度",
      dataIndex: "productRoasts",
      align: "center",
      width: 80,
      render: (productRoasts) => (
        <>
          {productRoasts && productRoasts?.length > 0
            ? productRoasts.map((roast) => (
                <Tag key={roast} color={COMMONS.PRIMARY_COLOR} className="m-1">
                  {roast || "ー"}
                </Tag>
              ))
            : "ー"}
        </>
      ),
    },
    {
      title: "挽き目",
      dataIndex: "productGrinds",
      align: "center",
      width: 80,
      render: (productGrinds) => (
        <>
          {productGrinds && productGrinds?.length > 0
            ? productGrinds.map((grind) => (
                <Tag key={grind} color={COMMONS.PRIMARY_COLOR} className="m-1">
                  {grind || "ー"}
                </Tag>
              ))
            : "ー"}
        </>
      ),
    },
    {
      title: "特徴",
      dataIndex: "productTastes",
      align: "center",
      width: 80,
      render: (productTastes) => (
        <>
          {productTastes && productTastes?.length > 0
            ? productTastes.map((taste) => (
                <Tag
                  key={taste}
                  color={COMMONS.PRIMARY_COLOR}
                  className="whitespace-normal m-1"
                >
                  {taste || "ー"}
                </Tag>
              ))
            : "ー"}
        </>
      ),
    },
  ]

  const handleTableChange = (paginationData, filters, sorter) => {
    if (paginationData.pageSize !== paginationPerPage) {
      setPaginationPerPage(paginationData.pageSize)
    }

    if (paginationData.current !== paginationPage) {
      setPaginationPage(paginationData.current)
    }

    if (sorter && sorter.order) {
      if (sorter.order === "ascend") {
        if (sorter.order !== paginationSort) {
          setPaginationSort("asc")
        }
      } else if (sorter.order === "descend") {
        if (sorter.order !== paginationSort) {
          setPaginationSort("desc")
        }
      }
    }

    if (sorter && sorter.field) {
      if (sorter.field !== paginationSortKey) {
        setPaginationSortKey(sorter.field)
      }
    }
  }

  const handleFilter = (data) => {
    if (data.filterProductCode !== filterProductCode) {
      setFilterProductCode(data.filterProductCode)
    }
    if (data.filterProductName !== filterProductName) {
      setFilterProductName(data.filterProductName)
    }
    if (data.filterIsDisplayed !== filterIsDisplayed) {
      setFilterIsDisplayed(data.filterIsDisplayed)
    }
  }

  const selectTagRender = (props) => {
    const { label, closable, onClose } = props

    const onPreventMouseDown = (event) => {
      event.preventDefault()
      event.stopPropagation()
    }

    return (
      <Tag
        color={COMMONS.PRIMARY_COLOR}
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
        style={{ marginRight: 3 }}
      >
        {label}
      </Tag>
    )
  }

  return (
    <>
      <Helmet>
        <title>商品一覧 | {process.env.REACT_APP_SYSTEM_NAME}</title>
      </Helmet>
      <Card
        title="商品一覧"
        className="animate__animated animate__fadeIn"
        bordered={false}
        extra={
          <Tooltip
            trigger={["hover"]}
            title="スマレジでの商品単価と同期"
            placement="bottom"
          >
            <Button
              icon={
                <FaSyncAlt
                  className={`anticon ${
                    syncPriceMutation.isLoading && "animate-spin"
                  }`}
                />
              }
              type="primary"
              size="large"
              onClick={handlePriceSync}
              disabled={syncPriceMutation.isLoading}
            >
              同期
            </Button>
          </Tooltip>
        }
      >
        <Row>
          <Col xs={24}>
            <Form
              form={productsFilterForm}
              onFinish={handleFilter}
              size="large"
              initialValues={{
                filterProductCode: undefined,
                filterProductName: undefined,
                filterIsDisplayed: undefined,
              }}
            >
              <Row gutter={[16, 16]} justify="start">
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  // lg={{ span: 8 }}
                  // xl={{ span: 6 }}
                >
                  <Form.Item
                    help="商品コード"
                    name="filterProductCode"
                    className="mb-4"
                  >
                    <Input
                      placeholder="161020"
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  // lg={{ span: 8 }}
                  // xl={{ span: 6 }}
                >
                  <Form.Item
                    help="商品名"
                    name="filterProductName"
                    className="mb-4"
                  >
                    <Input
                      placeholder="例：エチオピア モカ イルガチェフェ Ｇ－２"
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  // lg={{ span: 8 }}
                  // xl={{ span: 6 }}
                >
                  <Form.Item
                    help="端末表示"
                    name="filterIsDisplayed"
                    className="mb-4"
                  >
                    <Radio.Group>
                      {COMMONS.IS_DISPLAYED_TYPES.map((idt) => (
                        <Radio.Button key={idt.value} value={idt.value}>
                          {idt.text}
                        </Radio.Button>
                      ))}
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[8, 8]} justify="center" className="mt-8">
                <Col>
                  <Button type="primary" htmlType="submit" className="px-12">
                    検索
                  </Button>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
        <Divider />
        <div className="flex justify-start mb-4">
          <Button className="mr-1" onClick={showImportProductModal}>
            CSVインポート
          </Button>
          <Dropdown
            menu={{ items: exportOptions, onClick: exportHandler }}
            placement="bottom"
            trigger={["click"]}
            arrow={{ pointAtCenter: true }}
          >
            <Button
              icon={<FaChevronDown className="anticon" />}
              loading={
                exportProductCSVMutation.isLoading ||
                exportProductSampleCSVMutation.isLoading
              }
            >
              CSVエクスポート
            </Button>
          </Dropdown>
        </div>
        <Tooltip
          visible={isScrollableHintVisible}
          placement="topRight"
          title="⇦スクロールできます⇨"
          overlayStyle={{
            zIndex: 999,
          }}
        >
          <CustomTable
            ref={tableRef}
            bordered
            size="small"
            columns={productColumns}
            scroll={{
              x: 640,
              y: 720,
            }}
            locale={{ emptyText: "商品がありません" }}
            onChange={handleTableChange}
            pagination={{
              responsive: true,
              showSizeChanger: true,
              onShowSizeChange: (current, pageSize) =>
                setPaginationPerPage(pageSize),
              current: paginationPage,
              pageSize: paginationPerPage,
              total: paginationTotal,
              showTotal: (total, range) =>
                `全${total}件中${range[0]}～${range[1]}件目`,
              defaultCurrent: 1,
              defaultPageSize: 20,
              position: ["bottomRight"],
            }}
            dataSource={
              products
                ? products.map((product) => {
                    return {
                      key: product.productId || "ー",
                      isDisplayed: product.isDisplayed,
                      productId: product.productId || 0,
                      productImages: product.productImages || [],
                      productCode: product.productCode || "ー",
                      supplierProductNo: product.supplierProductNo || "ー",
                      productCategory: product?.Category?.categoryName || "ー",
                      productName: product.productName || "ー",
                      cost: product.cost || 0,
                      price: product.price || 0,
                      country: product.country || "ー",
                      recommendedRoast: product.recommendedRoast || "ー",
                      productRoasts: product.productRoasts || [],
                      productGrinds: product.productGrinds || [],
                      productTastes: product.productTastes || [],
                      action: product,
                    }
                  })
                : []
            }
          />
        </Tooltip>
      </Card>
      <Modal
        title={`商品詳細：${currentProduct?.productName || ""}`}
        visible={isProductDetailModalVisible}
        onCancel={hideProductDetailModal}
        footer={null}
        destroyOnClose
        centered
        bodyStyle={{ height: "90vh", overflow: "auto" }}
      >
        {currentProduct?.productImages &&
        currentProduct?.productImages?.length > 0 ? (
          <CarouselProvider
            naturalSlideWidth={100}
            naturalSlideHeight={100}
            totalSlides={currentProduct.productImages.length}
            visibleSlides={1}
            dragEnabled={false}
            lockOnWindowScroll
            infinite
            className="flex-col w-full"
          >
            <div className="relative">
              <Slider className="w-full" style={{ maxHeight: "350px" }}>
                {currentProduct.productImages.map((image) => (
                  <Slide key={image?.order} index={image?.order}>
                    {image?.picUrl ? (
                      <Image
                        src={`${API.UPLOADS_URL}${image?.picUrl}`}
                        renderError={
                          <Image
                            src="/default.jpg"
                            alt="商品画像"
                            style={{
                              maxHeight: "350px",
                            }}
                            className="rounded object-cover max-w-full"
                          />
                        }
                        alt="商品画像"
                        className="rounded object-cover max-w-full"
                        style={{ maxHeight: "350px" }}
                      />
                    ) : (
                      <Image
                        src="/default.jpg"
                        alt="商品画像"
                        style={{
                          maxHeight: "350px",
                        }}
                        className="rounded object-cover max-w-full"
                      />
                    )}
                  </Slide>
                ))}
              </Slider>
              <div className="absolute top-40 left-1">
                <ButtonBack className="rounded-full bg-white/80 border border-primary w-12">
                  <FaArrowLeft className="mx-1 my-4 text-primary align-middle" />
                </ButtonBack>
              </div>
              <div className="absolute top-40 right-1">
                <ButtonNext className="rounded-full bg-white/80 border border-primary w-12">
                  <FaArrowRight className="mx-1 my-4 text-primary align-middle" />
                </ButtonNext>
              </div>
            </div>
            <div className="mt-1">
              <CustomDotGroup>
                {currentProduct.productImages.map((image) => (
                  <Dot
                    key={image?.order}
                    slide={image?.order}
                    className="inline-block p-0 mr-1"
                  >
                    {image?.picUrl ? (
                      <Image
                        src={`${API.UPLOADS_URL}${image?.picUrl}`}
                        renderError={
                          <Image
                            src="/default.jpg"
                            alt="商品画像"
                            className="inline-block object-cover relative rounded"
                            style={{
                              maxHeight: "50px",
                              width: "auto",
                            }}
                          />
                        }
                        alt="商品画像"
                        className="inline-block object-cover relative rounded"
                        style={{
                          maxHeight: "50px",
                          width: "auto",
                        }}
                      />
                    ) : (
                      <Image
                        src="/default.jpg"
                        alt="商品画像"
                        className="inline-block object-cover rounded relative"
                        style={{
                          maxHeight: "50px",
                          width: "auto",
                        }}
                      />
                    )}
                  </Dot>
                ))}
              </CustomDotGroup>
            </div>
          </CarouselProvider>
        ) : (
          <AImage
            preview={false}
            src="/default.jpg"
            alt="商品画像"
            height={350}
            width="100%"
            className="object-cover max-w-full"
          />
        )}
        <Descriptions bordered column={2} layout="vertical" className="my-4">
          <Descriptions.Item label="表示状態" span={2}>
            {currentProduct?.isDisplayed ? "表示中" : "非表示中"}
          </Descriptions.Item>
          <Descriptions.Item label="ID">
            {currentProduct?.productId || "ー"}
          </Descriptions.Item>
          <Descriptions.Item label="商品コード">
            {currentProduct?.productCode || "ー"}
          </Descriptions.Item>
          <Descriptions.Item label="部門名">
            {currentProduct?.Category?.categoryName || "ー"}
          </Descriptions.Item>
          <Descriptions.Item label="品番">
            {currentProduct?.supplierProductNo || "ー"}
          </Descriptions.Item>
          <Descriptions.Item label="商品名" span={2}>
            {currentProduct?.productName || "ー"}
          </Descriptions.Item>
          <Descriptions.Item label="原価">{`${COMMONS.CURRENCY}${(
            Number(currentProduct?.cost) || 0
          ).toLocaleString()}`}</Descriptions.Item>
          <Descriptions.Item label="商品単価">{`${COMMONS.CURRENCY}${(
            Number(currentProduct?.price) || 0
          ).toLocaleString()}`}</Descriptions.Item>
          <Descriptions.Item label="おすすめの焙煎度" span={2}>
            {currentProduct?.recommendedRoast || "ー"}
          </Descriptions.Item>
          <Descriptions.Item label="おすすめ焙煎度の説明" span={2}>
            {currentProduct?.recommendedRoastText || "ー"}
          </Descriptions.Item>
          <Descriptions.Item label="原産国" span={2}>
            {currentProduct?.country || "ー"}
          </Descriptions.Item>
          <Descriptions.Item label="焙煎度" span={2}>
            {currentProduct?.productRoasts &&
            currentProduct?.productRoasts?.length > 0
              ? currentProduct.productRoasts.map((roast) => (
                  <Tag
                    key={roast?.label}
                    color={COMMONS.PRIMARY_COLOR}
                    className="m-1"
                  >
                    {roast?.label || "ー"}
                  </Tag>
                ))
              : "ー"}
          </Descriptions.Item>
          <Descriptions.Item label="挽き目" span={2}>
            {currentProduct?.productGrinds &&
            currentProduct?.productGrinds?.length > 0
              ? currentProduct.productGrinds.map((grind) => (
                  <Tag
                    key={grind?.label}
                    color={COMMONS.PRIMARY_COLOR}
                    className="m-1"
                  >
                    {grind?.label || "ー"}
                  </Tag>
                ))
              : "ー"}
          </Descriptions.Item>
          <Descriptions.Item label="特徴" span={2}>
            {currentProduct?.productTastes &&
            currentProduct?.productTastes?.length > 0
              ? currentProduct.productTastes.map((taste) => (
                  <Tag
                    key={taste?.label}
                    color={COMMONS.PRIMARY_COLOR}
                    className="whitespace-normal m-1"
                  >
                    {taste?.label || "ー"}
                  </Tag>
                ))
              : "ー"}
          </Descriptions.Item>
          <Descriptions.Item label="説明" span={2}>
            <p className="whitespace-pre-wrap">
              {currentProduct?.description || "ー"}
            </p>
          </Descriptions.Item>
        </Descriptions>
        <div className="flex justify-center">
          <Button size="large" onClick={hideProductDetailModal}>
            閉じる
          </Button>
        </div>
      </Modal>
      <Modal
        title={`商品編集：${currentProduct?.productName || ""}`}
        visible={isProductModalVisible}
        onCancel={hideProductModal}
        footer={null}
        destroyOnClose
        centered
        bodyStyle={{ height: "90vh", overflow: "auto" }}
      >
        {currentProduct ? (
          <Form
            form={productForm}
            name="productForm"
            onFinish={handleUpdateProduct}
            layout="vertical"
            requiredMark={false}
            preserve={false}
            size="large"
            initialValues={{
              isDisplayed: "true",
            }}
          >
            <Form.Item
              name="isDisplayed"
              label="表示状態"
              rules={[
                {
                  required: true,
                  message: "表示状態を選択してください",
                },
              ]}
            >
              <Select placeholder="表示状態を選択してください">
                <Option value="true">表示</Option>
                <Option value="false">非表示</Option>
              </Select>
            </Form.Item>
            <Form.List name="productImages" label="商品の画像">
              {(fields, { add, remove }) => (
                <DragDropContext onDragEnd={productImageOnDragEndHandle}>
                  <Droppable
                    droppableId="droppableImageMessage"
                    direction="vertical"
                  >
                    {(provided, snapshot) => (
                      <Row
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        justify="center"
                        align="middle"
                        gutter={[0, 8]}
                      >
                        {fields.map((field, index) => (
                          <Draggable
                            key={field.key + ""}
                            draggableId={field.key + ""}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <Col
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                span={24}
                              >
                                <Row
                                  align="middle"
                                  justify="center"
                                  gutter={[16, 16]}
                                >
                                  <Col flex="400px">
                                    <Form.Item
                                      {...field}
                                      key={[field.key, "image"]}
                                      name={[field.name, "image"]}
                                      fieldKey={[field.fieldKey, "image"]}
                                      valuePropName="file"
                                      className="mb-0 text-center"
                                      rules={[
                                        {
                                          required: true,
                                          message: "商品画像を選択してください",
                                        },
                                      ]}
                                    >
                                      <CustomUpload
                                        accept=".jpg, .jpeg, .png"
                                        listType="picture"
                                        maxCount={1}
                                        showUploadList={false}
                                        beforeUpload={() => {
                                          return false
                                        }}
                                        onChange={async (param) => {
                                          const image = {
                                            file: await RESIZE_FILE(
                                              param.file,
                                              "file",
                                            ),
                                            preview: await COMMONS.GET_BASE_64(
                                              param.file,
                                            ),
                                          }

                                          if (productImages[index]) {
                                            setProductImages(
                                              productImages.map((pi, i) =>
                                                i === index ? image : pi,
                                              ),
                                            )
                                          } else {
                                            setProductImages([
                                              ...productImages,
                                              image,
                                            ])
                                          }
                                        }}
                                      >
                                        {productImages[index] &&
                                        (productImages[index]?.preview ||
                                          productImages[index]?.picUrl) ? (
                                          <div className="bg-gray-100 rounded cursor-pointer border border-dashed border-gray-300 hover:border-primary transition">
                                            <img
                                              src={
                                                productImages[index]?.preview ||
                                                `${API.UPLOADS_URL}${productImages[index]?.picUrl}`
                                              }
                                              alt="商品画像"
                                              className="w-full max-w-full object-contain"
                                              style={{
                                                maxHeight: "150px",
                                              }}
                                            />
                                          </div>
                                        ) : (
                                          <Row
                                            justify="center"
                                            align="middle"
                                            style={{
                                              height: "150px",
                                            }}
                                            className="bg-gray-100 border border-dashed border-gray-300 rounded cursor-pointer"
                                          >
                                            <Col>
                                              <Button
                                                type="dashed"
                                                icon={
                                                  <FaCamera className="mr-1 anticon" />
                                                }
                                              >
                                                商品画像
                                              </Button>
                                            </Col>
                                          </Row>
                                        )}
                                      </CustomUpload>
                                    </Form.Item>
                                  </Col>
                                  {fields.length > 1 ? (
                                    <Col flex="40px" className="text-center">
                                      <Button
                                        danger
                                        icon={
                                          <FaTrashAlt className="text-red-600 anticon inline-block" />
                                        }
                                        onClick={() => {
                                          remove(field.name)
                                          setProductImages(
                                            productImages.filter(
                                              (ci, i) => i !== index,
                                            ),
                                          )
                                        }}
                                      />
                                    </Col>
                                  ) : null}
                                  <Divider />
                                </Row>
                              </Col>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        {productImages.length < 10 ? (
                          <Form.Item>
                            <Button
                              type="dashed"
                              onClick={() =>
                                productForm
                                  .validateFields()
                                  .then((values) => {
                                    add()
                                  })
                                  .catch((error) => {})
                              }
                              block
                              icon={<FaPlus className="mr-1 anticon" />}
                            >
                              商品画像追加
                            </Button>
                          </Form.Item>
                        ) : (
                          ""
                        )}
                      </Row>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </Form.List>
            <Form.Item name="productCountry" label="原産国">
              <Select
                allowClear
                className="w-full"
                placeholder="原産国を選択してください"
              >
                {COMMONS.COUNTRIES.map((country) => (
                  <Option key={country} value={country}>
                    {country}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="productRoasts" label="焙煎度オプション">
              <Select
                allowClear
                mode="multiple"
                className="w-full"
                placeholder="焙煎度オプションを選択してください"
                tagRender={selectTagRender}
              >
                {COMMONS.ROASTS.map((roast) => (
                  <Option key={roast} value={roast}>
                    {roast}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="productRecommendedRoast" label="おすすめ焙煎度">
              <Select
                allowClear
                className="w-full"
                placeholder="おすすめ焙煎度を選択してください"
              >
                {COMMONS.ROASTS.map((roast) => (
                  <Option key={roast} value={roast}>
                    {roast}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="productRecommendedRoastText"
              label="おすすめ焙煎度の説明"
            >
              <Input
                allowClear
                className="w-full"
                placeholder="おすすめ焙煎度の説明を入力してください"
              />
            </Form.Item>
            <Form.Item name="productGrinds" label="挽き目オプション">
              <Select
                allowClear
                mode="multiple"
                className="w-full"
                placeholder="挽き目オプションを選択してください"
                tagRender={selectTagRender}
              >
                {COMMONS.GRINDS.map((grind) => (
                  <Option key={grind} value={grind}>
                    {grind}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="productTastes" label="特徴">
              <Select
                allowClear
                mode="multiple"
                className="w-full"
                placeholder="特徴を選択してください"
                tagRender={selectTagRender}
              >
                {COMMONS.TASTES.map((taste) => (
                  <Option key={taste} value={taste}>
                    {taste}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Divider />
            <div className="flex justify-center">
              <Button className="m-1" onClick={hideProductModal}>
                閉じる
              </Button>
              <Button
                className="m-1"
                type="primary"
                htmlType="submit"
                loading={updateProductMutation.isLoading}
              >
                保存
              </Button>
            </div>
          </Form>
        ) : (
          ""
        )}
      </Modal>
      <Modal
        title="CSVインポート"
        visible={isImportProductModalVisible}
        onCancel={hideImportProductModal}
        footer={null}
        destroyOnClose
        centered
        width={720}
        bodyStyle={{ maxHeight: "90vh", overflow: "auto" }}
      >
        <CSVReader
          onUploadAccepted={(results) => {
            setCsvHasError(false)

            if (results && results?.data && results?.data?.length > 0) {
              setCsvProducts(
                results.data.map((row) => {
                  if (!row["商品コード"]) {
                    setCsvHasError(true)
                  }

                  return {
                    productCode: row["商品コード"],
                    isDisplayed: row["表示"],
                    country: row["原産国"],
                    recommendedRoast: row["おすすめ焙煎度"],
                    recommendedRoastText: row["おすすめ焙煎度の説明"],
                    productRoasts: row["焙煎度"],
                    productGrinds: row["挽き目"],
                    productTastes: row["特徴"],
                  }
                }),
              )
            } else {
              setCsvProducts([])
            }
          }}
          noDrag
          config={{
            header: true,
          }}
        >
          {({
            getRootProps,
            acceptedFile,
            ProgressBar,
            getRemoveFileProps,
          }) => (
            <div className="flex flex-col">
              <div className="flex justify-center mb-4">
                <div className="bg-yellow-300 rounded p-4">
                  <p>商品データのCSVをアップロードしてください</p>
                </div>
              </div>
              <div className="flex flex-col">
                <div className="flex justify-center">
                  <Button {...getRootProps()}>CSVアップロード</Button>
                </div>
                <div>
                  {acceptedFile ? (
                    <div className="flex flex-col border border-gray-300 rounded p-4 mt-4">
                      <p className="text-base">
                        <FaFileCsv />
                        <span className="ml-2">{acceptedFile.name}</span>
                      </p>
                      <div className="flex justify-center">
                        <Button
                          danger
                          icon={
                            <FaTrashAlt style={{ verticalAlign: "inherit" }} />
                          }
                          {...getRemoveFileProps({
                            onClick: () => {
                              setCsvProducts([])
                              setCsvHasError(false)
                            },
                          })}
                        />
                      </div>
                    </div>
                  ) : (
                    ""
                  )}
                </div>
                <ProgressBar
                  style={{ backgroundColor: COMMONS.PRIMARY_COLOR }}
                />
              </div>
            </div>
          )}
        </CSVReader>
        <Divider />
        {csvHasError && (
          <div className="flex justify-center mb-4">
            <div className="bg-red-300 rounded p-4">
              <p>「商品コード」がない行があります。</p>
            </div>
          </div>
        )}
        <Table
          bordered
          size="small"
          columns={importProductColumns}
          locale={{ emptyText: "データがありません" }}
          scroll={{
            x: 640,
            y: 720,
          }}
          pagination={{
            responsive: true,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `全${total}件中${range[0]}～${range[1]}件目`,
            defaultCurrent: 1,
            defaultPageSize: 10,
            position: ["bottomCenter"],
          }}
          dataSource={
            csvProducts
              ? csvProducts.map((row) => {
                  return {
                    key: row.productCode,
                    productCode: row.productCode || "ー",
                    isDisplayed: row.isDisplayed ?? "ー",
                    country: row.country || "ー",
                    recommendedRoast: row.recommendedRoast || "ー",
                    recommendedRoastText: row.recommendedRoastText || "ー",
                    productRoasts: row.productRoasts || "ー",
                    productGrinds: row.productGrinds || "ー",
                    productTastes: row.productTastes || "ー",
                  }
                })
              : []
          }
        />
        <Divider />
        <div className="flex justify-center">
          <Button
            disabled={csvProducts.length > 0 ? false : true}
            loading={importProductsMutation.isLoading}
            type="primary"
            onClick={handleImportProduct}
          >
            確認する
          </Button>
        </div>
      </Modal>
      <Modal
        title="CSVインポート確認"
        visible={isImportProductConfirmModalVisible}
        onCancel={hideImportProductConfirmModal}
        footer={null}
        destroyOnClose
        centered
        width={720}
        bodyStyle={{ maxHeight: "90vh", overflow: "auto" }}
      >
        <Tabs
          defaultActiveKey={
            csvValidProducts.length > 0
              ? "valid"
              : csvInvalidProducts.length > 0
                ? "invalid"
                : ""
          }
        >
          <TabPane
            tab={
              <Badge
                status="success"
                text={`有効（${csvValidProducts.length}件）`}
              />
            }
            disabled={!csvValidProducts.length > 0}
            key="valid"
          >
            <div className="flex justify-center">
              <div className="bg-yellow-300 rounded p-4">
                <p>以下の商品リストはすべて保存されます</p>
              </div>
            </div>
            <Divider />
            <Table
              bordered
              size="small"
              columns={importProductConfirmColumns}
              locale={{ emptyText: "データがありません" }}
              scroll={{
                x: 640,
                y: 720,
              }}
              pagination={{
                responsive: true,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `全${total}件中${range[0]}～${range[1]}件目`,
                defaultCurrent: 1,
                defaultPageSize: 10,
                position: ["bottomCenter"],
              }}
              dataSource={
                csvValidProducts
                  ? csvValidProducts.map((row) => {
                      return {
                        key: row.productCode,
                        productCode: row.productCode || "ー",
                        isDisplayed: row.isDisplayed ?? "ー",
                        country: row.country || "ー",
                        recommendedRoast: row.recommendedRoast || "ー",
                        recommendedRoastText: row.recommendedRoastText || "ー",
                        productRoasts: row.productRoasts || "ー",
                        productGrinds: row.productGrinds || "ー",
                        productTastes: row.productTastes || "ー",
                      }
                    })
                  : []
              }
            />
          </TabPane>
          <TabPane
            tab={
              <Badge
                status="error"
                text={`無効（${csvInvalidProducts.length}件）`}
              />
            }
            disabled={!csvInvalidProducts.length > 0}
            key="invalid"
          >
            <div className="flex justify-center">
              <div className="bg-yellow-300 rounded p-4">
                <p>
                  以下の商品リストには必要な情報が足りないため、保存されることができません
                </p>
              </div>
            </div>
            <Divider />
            <Table
              bordered
              size="small"
              columns={importProductConfirmColumns}
              locale={{ emptyText: "データがありません" }}
              scroll={{
                x: 640,
                y: 720,
              }}
              pagination={{
                responsive: true,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `全${total}件中${range[0]}～${range[1]}件目`,
                defaultCurrent: 1,
                defaultPageSize: 10,
                position: ["bottomCenter"],
              }}
              dataSource={
                csvInvalidProducts
                  ? csvInvalidProducts.map((row) => {
                      return {
                        key: row.productCode,
                        productCode: row.productCode || "ー",
                        isDisplayed: row.isDisplayed ?? "ー",
                        country: row.country || "ー",
                        recommendedRoast: row.recommendedRoast || "ー",
                        recommendedRoastText: row.recommendedRoastText || "ー",
                        productRoasts: row.productRoasts || "ー",
                        productGrinds: row.productGrinds || "ー",
                        productTastes: row.productTastes || "ー",
                      }
                    })
                  : []
              }
            />
          </TabPane>
        </Tabs>
        <Divider />
        <div className="flex justify-center">
          <Button onClick={hideImportProductConfirmModal} className="m-1">
            閉じる
          </Button>
          <Button
            disabled={csvValidProducts.length > 0 ? false : true}
            loading={importProductsMutation.isLoading}
            type="primary"
            className="m-1"
            onClick={handleImportProductConfirm}
          >
            インポート
          </Button>
        </div>
      </Modal>
    </>
  )
}

export default Products
